import { useLazyQuery } from '@apollo/client';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  Input,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Alert } from '@material-ui/lab';
import { DatePicker } from '@material-ui/pickers';
import {
  convertLocalToISOInTimeZone,
  timeOnly,
  updateDateWithTimeString,
  validPhone,
} from '@onwardcare/core';
import GooglePlacesAutocomplete from 'components/GooglePlacesAutocomplete';
import { LabelWithTooltip } from 'components/LabelWithTooltip';
import Map from 'components/Map';
import RideEstimate from 'components/rides/RideEstimate';
import onwardColors from 'lib/onwardColors';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import * as stripeJs from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { StripeForm } from '../layout/StripeForm';
import { RideFormType, RideFormTypeSubmit } from 'pages/types';
import type {
  Exact,
  FinancialResponsibilityChoice,
  Ride,
  Rider,
  ScheduleQueryWithRideQuery,
} from 'generated/graphql';
import {
  CreateMncsInput,
  CreateRecurringInput,
  FieldError,
  Maybe,
  PayerOption,
  StripeSetupIntentQueryDocument,
  useAccountFinancialResponsibilityChoicesLazyQuery,
  useRideSuggestedLocationsQueryLazyQuery,
  useRideNotesPlaceholderTextQueryLazyQuery,
  useRiderAvailablePayerTypesQueryLazyQuery,
  useRiderProfileQueryLazyQuery,
  useValidateSmsCapabilityLazyQuery,
  useValidateLocationGeoMutationMutation,
} from 'generated/graphql';
import type { ApolloQueryResult } from '@apollo/client/core/types';
import { Stripe } from '@stripe/stripe-js/types/stripe-js';
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';
import { TransportTypeOptions } from './rideFormComponents';
import {
  TransportTypeFeeOptionsComponent,
  useTransportTypeOptions,
} from './rideFormComponents/TransportTypeComponent';
import { AddMedicalNecessityStatement } from './rideFormComponents/AddMedicalNecessityStatement/AddMedicalNecessityStatement';
import { useStyles } from './rideFormStyles';
import FinancialResponsibilityForm from './rideFormComponents/financialResponsibility/FinancialResponsibilityForm';
import { BedSideNurseComponent } from './rideFormComponents/BedSideComponent/BedSideNurseComponent';
import { TransportTypeSelector } from './rideFormComponents/TransportTypeSelector/TransportTypeSelector';
import RiderInputComponent from './RiderInputComponent';
import HelpInformation from 'components/HelpInformation';
import SessionInformation from 'components/SessionInformation';
import { RideshareComponents } from './rideFormComponents/Rideshare';
import { useVetridesContext } from '../../contexts/vetrides-context';
import { INCHES_PER_CM } from './rideFormComponents/constants';
import {
  getCmFromInches,
  getKgFromLBS,
  getLBSFromKg,
} from './rideFormComponents/helpers';
import { TransportReasonCategories } from './rideFormComponents/TransportReasonCategories/TransportReasonCategories';
import { ConfirmNotifyDialog } from './rideFormComponents/ConfirmDialogNotify/ConfirmNotifyDialog';
import { useEhrContext } from '../../contexts/ehr-context';
import { readIsMetric } from '../../storages/is-metric-storage';
import {
  deleteSession,
  readSessionObject,
  writeSessionObject,
} from '../../storages/session-storage';
import { LocationPhoto } from './LocationPhoto';
import { useSessionContext } from '../../contexts/session-context';
import {
  addDays,
  addHours,
  addMonths,
  addWeeks,
  differenceInMinutes,
  isBefore,
  isSameDay,
  startOfHour,
} from 'date-fns';
import { TooltipButton } from 'components/TooltipButton';
import { RideTime } from './RideTime';
import InvoiceRide from './rideFormComponents/InvoiceRide';
import {
  usePaymentMethods,
  SPLIT_PAY_PAYER_TYPE,
} from './rideFormComponents/hooks';
import { CurrentPayerType } from './rideFormComponents/types';

const DEFAULT_LATITUDE = 37.84073;
const DEFAULT_LONGITUDE = -122.251691;
const FORM_KEY = 'transportationForm';

const WHEELCHAIR_BOUND_RIDER_MOBILITY = 'wheelchair_bound';
const WHEELCHAIR_BOUND_TRANSPORT_TYPES = [
  'gurney',
  'wheelchair',
  'bls_ambulance',
  'cct_ambulance',
  'als_ambulance',
];

/**
 * Delete the saved form data from the session storage.
 */
const deleteSavedFormData = () => {
  deleteSession(FORM_KEY);
};
/**
 * Read the saved form data from the session storage.
 *
 * @returns The saved form data from the session storage.
 */
const readSavedFormData = () => {
  return readSessionObject(FORM_KEY) as Record<string, any> | null;
};
/**
 * Write the form data to the session storage.
 *
 * @param data The form data to write to the session storage.
 */
const writeFormData = (data: object) => {
  writeSessionObject(FORM_KEY, data);
};

const rideTypes = [
  { type: 'oneway', text: 'One Way' },
  { type: 'roundtrip', text: 'Wait and Return' },
  { type: 'delivery', text: 'Delivery' },
];

const trueAddress = (e: any) => {
  return !!e['zipcode'];
};

const defaultStartLocation = (defaultRide?: Ride) => {
  if (defaultRide === undefined) {
    return null;
  }

  const { startLocationPhoto } = defaultRide;
  const { ...startLocationFields } = defaultRide.startLocation;
  return {
    ...startLocationFields,
    locationPhoto: startLocationPhoto,
  };
};

const defaultEndLocation = (defaultRide?: Ride) => {
  if (defaultRide === undefined) {
    return null;
  }

  const { endLocationPhoto } = defaultRide;
  const { ...endLocationFields } = defaultRide.endLocation;
  return {
    ...endLocationFields,
    locationPhoto: endLocationPhoto,
  };
};

export type RideFormProps = {
  rideErrorsState?: Maybe<Array<FieldError>>;
  createError: string;
  onSubmit: (data: RideFormType) => void;
  riders: ScheduleQueryWithRideQuery['riders'];
  defaultRide: Ride;
  recurringOptions?: Maybe<CreateRecurringInput>;
  defaultRiderId?: string;
  isSubmitting: boolean;
  actionType: 'update' | 'create';
  useDefaultRideRequestedStartTime?: boolean;
  getAccountData?:
    | ((
        variables?: Partial<Exact<{ rideId: number }>> | undefined,
      ) => Promise<ApolloQueryResult<ScheduleQueryWithRideQuery>>)
    | undefined;
  isReturnTrip?: boolean;
  mncsDefaultAnswers?: Record<string, any>;
};

const REQUESTED_START_GAP_MINUTES = 60;

// TODO: in future we should use an opportunity to consolidate defaultRide?.rider?.id and watchRide.riderId
export const RideForm: FC<RideFormProps> = ({
  createError,
  onSubmit,
  riders,
  defaultRide,
  defaultRiderId: defaultRiderIdProp,
  isSubmitting,
  actionType,
  useDefaultRideRequestedStartTime,
  getAccountData,
  isReturnTrip,
  recurringOptions,
  rideErrorsState,
  mncsDefaultAnswers,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { isEhrPatientContext } = useEhrContext();
  const { isVetride } = useVetridesContext();
  const { isAdmin, session } = useSessionContext();
  const { account, profile } = session ?? {};
  const timeZone = account?.tzName ?? undefined;

  const showSessionInfo = isEhrPatientContext || isVetride;

  const { search } = useLocation();
  const [bufferTime, setBufferTime] = useState<number | undefined>(undefined);

  const isMetric = readIsMetric();
  const [useMetric, setUseMetric] = useState(isMetric);

  const [ridersList, setRidersList] = useState<Rider[] | null | undefined>(
    riders,
  );
  const riderId = defaultRiderIdProp || defaultRide?.rider?.id;
  // Pulling the riderId from the URL query params or the route params is only
  // used for when to clear the saved form data. Using this instead of the
  // `riderId` variable in the line above will make sure we don't use the rider
  // id when they are loading a Vetrides request. Otherwise, it will not
  // properly delete the old saved data and you'll get bad data in the form.
  const urlParams = new URLSearchParams(search);
  const riderIdQueryParam = urlParams.get('riderID');
  const { riderId: riderIdRouteParam } = useParams<{
    riderId?: string;
  }>();
  const riderIdParam = riderIdQueryParam || riderIdRouteParam;

  // This is probably used for consumer accounts that only have one rider.
  const defaultRider =
    ridersList && ridersList.length === 1 ? ridersList[0] : null;
  // This style is used to either show or hide the cost center field if there is
  // only one cost center available.
  const [singleCostCenterStyle, setSingleCostCenterStyle] =
    useState('inline-flex');

  /**
   * The persisted flow is temporarily disabled to prevent data inconsistencies.
   */
  // const transportationForm = readSavedFormData() || {};
  const transportationForm = {};

  const [getSetupIntent, { data: stripeSetupIntentData }] = useLazyQuery(
    StripeSetupIntentQueryDocument,
    {
      fetchPolicy: 'no-cache',
    },
  );

  const [stripeData, setStripeData] = useState(null);
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const [stripeOptions, setStripeOptions] =
    useState<stripeJs.StripeElementsOptions | null>(null);

  /**
   * The persisted flow is temporarily disabled to prevent data inconsistencies.
   */
  // useEffect(() => {
  //   if (!riderIdParam) {
  //     // There is an outstanding issue with keeping the form data as long as
  //     // there is a `riderId` property. If the user fills out the form, then
  //     // leaves the page, then they go to the Rider Profile page and click the
  //     // "New Ride" button, it will have a `riderId` property, so it won't clear
  //     // the form data. That means that it will load the data from the previous
  //     // ride, which isn't what we want.
  //     //
  //     // Also, if you fill out the form, then click the "Add rider" or "Edit
  //     // rider" button, then come back, it will correctly populate the form. But
  //     // if you click one of those buttons again and come back, the form will
  //     // be back to empty.
  //     deleteSavedFormData();
  //   }
  // }, [riderIdParam]);

  useEffect(() => {
    if (stripeSetupIntentData) {
      setStripeData(stripeSetupIntentData);
      setStripePromise(
        loadStripe(stripeSetupIntentData?.stripeSetupIntent?.publishableKey),
      );
      setStripeOptions({
        locale: 'en',
        clientSecret: stripeSetupIntentData?.stripeSetupIntent?.setupIntent,
        appearance: {
          theme: 'flat',
          variables: {
            borderRadius: '0px',
          },
        },
      });
    }
  }, [stripeSetupIntentData]);

  const handleClick = () => {
    if (currentPayer?.id) {
      getSetupIntent({
        variables: { ownerId: currentPayer.id, ownerType: currentPayer.type },
      });
    }
  };

  const [showInvoiceRideLabel, setShowInvoiceRideLabel] =
    useState<boolean>(true);
  const [showStartInstructions, setShowStartInstructions] = useState(false);
  const [showEndInstructions, setShowEndInstructions] = useState(false);
  const [requestedStartTime, setRequestedStartTime] = useState<Date | null>(
    null,
  );
  const [requestedEndTime, setRequestedEndTime] = useState<Date | null>(null);

  const [availablePayerTypes, setAvailablePayerTypes] = useState<
    PayerOption[] | []
  >(profile?.availablePayerTypes || []);

  const [
    accountFinancialResponsibilityChoices,
    setAccountFinancialResponsibilityChoices,
  ] = useState<FinancialResponsibilityChoice[] | []>([]); // list of current account's financial responsibility choices fetched from backend

  const [
    rideFinancialResponsibilityChoice,
    setRideFinancialResponsibilityChoice,
  ] = useState<FinancialResponsibilityChoice | null>(null); // currently selected financial responsibility choice for the ride

  const [currentRider, setCurrentRider] = useState<Rider | null>(null); // currently selected rider for the ride
  // timestamp to indicate when a rider was last fetched from the backend
  // it used as a trigger to update rider payment methods collection whenever we (for example) create a new payment method for the rider on frontend
  const [paymentDataFetchedAt, setPaymentDataFetchedAt] = useState<Date | null>(
    null,
  );

  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState<
    string | null
  >(null); // default payment method id for the ride form. We update it whenever collection of available payment methods is changing

  const [showStartLocationPhoto, setShowStartLocationPhoto] = useState(true);
  const [showEndLocationPhoto, setShowEndLocationPhoto] = useState(true);
  const [shouldShowFulfillmentNotice, setShouldShowFulfillmentNotice] =
    useState(false);

  const [startLocationGeoWarning, setStartLocationGeoWarning] = useState<string | null>(null);
  const [endLocationGeoWarning, setEndLocationGeoWarning] = useState<string | null>(null);

  const lastValidatedStartCoords = useRef<{ latitude: number; longitude: number } | null>(null);
  const lastValidatedEndCoords = useRef<{ latitude: number; longitude: number } | null>(null);

  const [showRecurringOptions, setShowRecurringOptions] = useState(false);

  const toggleRecurringOptions = () => {
    setShowRecurringOptions(!showRecurringOptions);
  };

  const heightInchesSaved = transportationForm?.heightInches || 0;
  const weightSaved = transportationForm?.weightLbs;
  const sourceHeightInches =
    (riderId ? heightInchesSaved : defaultRide?.heightInches) || 0;
  const heightInch = sourceHeightInches ? sourceHeightInches % 12 : '';
  const heightFeet = sourceHeightInches
    ? Math.trunc(sourceHeightInches / 12)
    : '';
  const heightInches = sourceHeightInches || '';
  const weightLbs = riderId ? weightSaved || 0 : defaultRide?.weightLbs || 0;

  const heightCm = getCmFromInches(heightInches);
  const multipleAvailablePayerTypes = availablePayerTypes.length > 1;

  const allTransportTypes = useMemo(
    () =>
      profile?.availableTransportTypes &&
      profile.availableTransportTypes.length >= 1
        ? profile.availableTransportTypes
        : [],
    [profile?.availableTransportTypes],
  );

  const wheelchairBoundTransportTypes = useMemo(
    () =>
      allTransportTypes.filter(type =>
        WHEELCHAIR_BOUND_TRANSPORT_TYPES.includes(type.name),
      ),
    [allTransportTypes],
  );
  const [availableTransportTypes, setAvailableTransportTypes] =
    useState(allTransportTypes);

  const defaultTransportTypeName =
    availableTransportTypes.length >= 1 ? availableTransportTypes[0].name : '';
  const isUpdate = actionType === 'update';
  const isCreate = actionType === 'create';

  // default times should be in existing timezone if one exists
  const defaultValues = {
    ride: {
      rideType: defaultRide?.rideType || 'oneway',
      firstAvailable: defaultRide?.firstAvailable || false,
      riderId: riderId ? riderId : undefined,
      requestedStartTime:
        isUpdate || useDefaultRideRequestedStartTime === true
          ? new Date(defaultRide?.requestedStartTime)
          : addHours(new Date(), 2),
      startLocation: defaultStartLocation(defaultRide),
      endLocation: defaultEndLocation(defaultRide),
      passengerCount: defaultRide?.passengerCount || 1,
      transportType: defaultRide?.transportType || defaultTransportTypeName,
      flexibleRide: defaultRide?.flexibleRide || false,
      notes: defaultRide?.notes,
      deliverySubstitution: defaultRide
        ? defaultRide.deliverySubstitution
        : 'yes',
      paymentMethodId: defaultPaymentMethodId,
      bariatric: defaultRide?.bariatric,
      stairsCount: defaultRide?.stairsCount,
      weight: useMetric ? getKgFromLBS(weightLbs) : weightLbs,
      heightInch: heightInch,
      heightFeet: heightFeet,
      heightInches: heightInches,
      heightCm: heightCm,
      payerType: riderId
        ? transportationForm?.payerType
        : defaultRide?.payerType ||
          (multipleAvailablePayerTypes && availablePayerTypes?.[0].name),
      payer: defaultRide?.payer,
      riderWillUseProvidersWheelchair:
        defaultRide?.riderWillUseProvidersWheelchair
          ? defaultRide.riderWillUseProvidersWheelchair
            ? 1
            : 0
          : 1,
      pickupRoomNumber: defaultRide?.pickupRoomNumber || null,
      dropoffRoomNumber: defaultRide?.dropoffRoomNumber || null,
      costCenter: riderId
        ? transportationForm?.costCenter || null
        : defaultRide?.costCenter || null,
      oxygenRequired: defaultRide?.oxygenRequired,
      contactPrecautionsRequired: defaultRide?.contactPrecautionsRequired,
      transportReason: defaultRide?.transportReason,
      transportReasonComment: defaultRide?.transportReasonComment,
      transportReasonAppointmentTime: defaultRide?.appointmentTime,
    },
    startTime:
      isUpdate || useDefaultRideRequestedStartTime === true
        ? timeOnly(defaultRide?.requestedStartTime, {
            timeZone,
            timeFormat: '24',
            hideTimeZone: true,
          })
        : timeOnly(startOfHour(addHours(new Date(), 2)), {
            timeZone,
            timeFormat: '24',
            hideTimeZone: true,
          }),
    endTime:
      isUpdate || useDefaultRideRequestedStartTime === true
        ? timeOnly(defaultRide?.requestedEndTime, {
            timeZone,
            timeFormat: '24',
            hideTimeZone: true,
          })
        : timeOnly(startOfHour(addHours(new Date(), 3)), {
            timeZone,
            timeFormat: '24',
            hideTimeZone: true,
          }),

    recurringOptions: recurringOptions || {
      interval: 1,
      repeat: 'weekly',
      untilDate: addWeeks(new Date(), 4),
      dow: null,
      notes: null,
    },
    mncsOptions: mncsDefaultAnswers || ({} as Record<string, any>),
    bedsideNurse: defaultRide?.bedsideNurse || {},
    appointmentTimeTransportReason: {
      startAppointmentDay: defaultRide?.appointmentTime
        ? new Date(defaultRide?.appointmentTime)
        : addHours(new Date(), 2),
      startAppointmentTime: timeOnly(
        defaultRide?.appointmentTime
          ? new Date(defaultRide?.appointmentTime)
          : addHours(new Date(), 2),
        { timeZone, timeFormat: '24' },
      ),
    },
  };

  const {
    handleSubmit,
    errors,
    control,
    watch,
    register,
    setValue,
    getValues,
    formState,
    clearErrors,
    trigger,
    setError,
  } = useForm<typeof defaultValues>({
    defaultValues,
  });

  useEffect(() => {
    if (rideErrorsState) {
      rideErrorsState.forEach(rError => {
        setError(`ride.${rError.field}`, {
          message: rError.messages.join('\n'),
          type: 'custom',
        });
      });
    }
  }, [rideErrorsState, setError]);

  // Prevent location fields from being reset to defaultRide values on validation
  // errors, which would wipe out any user changes made after the component mounts.
  const hasSetInitialLocations = useRef(false);

  useEffect(() => {
    if (
      defaultRide?.startLocation &&
      defaultRide?.endLocation &&
      !hasSetInitialLocations.current
    ) {
      const endLocationInstructions =
        // @ts-ignore
        defaultRide.endLocationInstructions ||
        defaultRide.endLocation.instructions;
      const startLocationInstructions =
        // @ts-ignore
        defaultRide.startLocationInstructions ||
        defaultRide.startLocation.instructions;

      setShowEndInstructions(!!endLocationInstructions);
      setShowStartInstructions(!!startLocationInstructions);

      setValue('ride.endLocationInstructions', endLocationInstructions);
      setValue('ride.startLocationInstructions', startLocationInstructions);
      setValue('ride.startLocation', defaultStartLocation(defaultRide));
      setValue('ride.endLocation', defaultEndLocation(defaultRide));

      hasSetInitialLocations.current = true;
    }
  }, [
    setValue,
    defaultRide,
    defaultRide?.startLocation,
    defaultRide?.endLocation,
  ]);

  useEffect(() => {
    setValue('ride.pickupRoomNumber', defaultRide?.pickupRoomNumber ?? null);
    setValue('ride.dropoffRoomNumber', defaultRide?.dropoffRoomNumber ?? null);
  }, [setValue, defaultRide?.dropoffRoomNumber, defaultRide?.pickupRoomNumber]);

  const watchRide = watch('ride');
  const watchStartTime = watch('startTime');
  const watchEndTime = watch('endTime');
  const watchTransportType = watchRide.transportType
    ? watchRide.transportType
    : defaultValues.ride.transportType;
  const isRideshare = watchTransportType === 'rideshare';

  const [showMedicalNecessity, setShowMedicalNecessity] = useState(false);

  const onFormSubmit = e => {
    e.preventDefault();
    clearErrors();
    handleSubmit(submit)();
  };

  /**
   * The persisted flow is temporarily disabled to prevent data inconsistencies.
   */
  // useEffect(() => {
  //   if (!isEmpty(formState?.dirtyFields)) {
  //     const weightLbs = useMetric // save always as LBS
  //       ? getLBSFromKg(watchRide.weight)
  //       : watchRide.weight;

  //     let transportationCopy = {
  //       ...watchRide,
  //       weightLbs,
  //       startTime: '',
  //       endTime: '',
  //     };

  //     // TypeScript is complaining that we are assigning a `string` to a `Date`
  //     // object. This is fine because we want to store the string version to
  //     // storage. When we restore it, we convert it back to a `Date` object.
  //     // @ts-ignore
  //     transportationCopy.requestedStartTime =
  //       watchRide.requestedStartTime?.toISOString();
  //     transportationCopy.startTime = watchStartTime ?? '';
  //     transportationCopy.endTime = watchEndTime ?? '';

  //     writeFormData(transportationCopy);
  //   }
  // }, [watchRide, watchStartTime, watchEndTime, formState, useMetric]);

  useEffect(() => {
    if (watchRide.transportType === 'rideshare') {
      setValue('ride.rideType', 'oneway');
    }

    if (!!watchTransportType.match('ambulance')) {
      const selectedPayerType = availablePayerTypes?.find(
        payerType => payerType.promptForPayerDemographics === false,
      );

      if (selectedPayerType) {
        setValue('ride.payerType', selectedPayerType.name);
      }
    } else if (availablePayerTypes.length === 1) {
      if (
        !watchRide.payerType ||
        watchRide.payerType !== availablePayerTypes[0].name
      ) {
        setValue('ride.payerType', availablePayerTypes[0].name);
      }
    }
  }, [watchRide.transportType, setValue, availablePayerTypes]);

  useEffect(() => {
    // Set the default end time to be 1 hour after the start time when roundtrip
    // is selected. Don't do this for updates or when it's requested we maintain
    // the passed in ride's times (used for return trips) because we need it to
    // be set to the end time the user selected.
    if (
      watchRide.rideType === 'roundtrip' &&
      !isUpdate &&
      !useDefaultRideRequestedStartTime
    ) {
      // We don't want to convert to the time zone here because we are just
      // adding an hour to the time the user typed in.
      const endTime = timeOnly(
        addHours(updateDateWithTimeString(new Date(), watchStartTime), 1),
        {
          timeFormat: '24',
          hideTimeZone: true,
        },
      );
      control.setValue('endTime', endTime);
    }
    // We don't want to include the `watchStartTime` here because we only want
    // this to fire when the ride type or action changes to initialize it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchRide.rideType, isUpdate, useDefaultRideRequestedStartTime]);

  const filteredRideTypes = useMemo(() => {
    if (watchRide.transportType === 'rideshare') {
      return [{ type: 'oneway', text: 'One Way' }];
    }

    return rideTypes;
  }, [watchRide.transportType]);

  const watchrecurringOptions = watch('recurringOptions');
  const watchmncsOptions = watch('mncsOptions');

  // requested start and end are calculated based on the date and time fields
  useEffect(() => {
    if (watchRide.requestedStartTime && watchStartTime) {
      setRequestedStartTime(
        updateDateWithTimeString(watchRide.requestedStartTime, watchStartTime),
      );
    }
  }, [watchRide.requestedStartTime, watchStartTime, timeZone]);

  useEffect(() => {
    if (watchRide.requestedStartTime && watchEndTime && watchRide.rideType) {
      if (watchRide.rideType === 'roundtrip') {
        setRequestedEndTime(
          updateDateWithTimeString(watchRide.requestedStartTime, watchEndTime),
        );
      } else {
        setRequestedEndTime(null);
      }
    }
  }, [
    watchRide.requestedStartTime,
    watchEndTime,
    watchRide.rideType,
    timeZone,
  ]);

  useEffect(() => {
    if (
      watchRide.heightInch !== undefined ||
      watchRide.heightInch !== null ||
      watchRide.heightFeet !== undefined ||
      watchRide.heightFeet !== null
    ) {
      // @ts-ignore
      if (watchRide.heightFeet === '' && watchRide.heightInch === '') {
        setValue('ride.heightInches', '');
        trigger('ride.heightInches');
        return;
      }

      const heightInches =
        (watchRide?.heightFeet || 0) * 12 + (watchRide.heightInch || 0);
      setValue('ride.heightInches', heightInches);
      trigger('ride.heightInches');
    }
  }, [setValue, trigger, watchRide.heightInch, watchRide.heightFeet]);

  const [getPickupSuggestedLocations, { data: dataPickupSuggestedLocations }] =
    useRideSuggestedLocationsQueryLazyQuery();

  const [getEndSuggestedLocations, { data: dataEndSuggestedLocations }] =
    useRideSuggestedLocationsQueryLazyQuery();

  const [getRiderAvailablePayerTypes, { data: riderAvailablePayerTypesData }] =
    useRiderAvailablePayerTypesQueryLazyQuery();

  const [getRideNotesPlaceholder, { data: rideNotesPlaceholderData }] =
    useRideNotesPlaceholderTextQueryLazyQuery();

  const { data: transportTypeOptionsData } =
    useTransportTypeOptions(watchTransportType);

  useEffect(() => {
    getRideNotesPlaceholder({
      variables: { transportType: watchTransportType },
    });
  }, [getRideNotesPlaceholder, watchTransportType]);

  // if the rider or account changes update the recent locations and payer types
  useEffect(() => {
    getPickupSuggestedLocations({
      variables: {
        riderId: watchRide.riderId,
        transportType: watchTransportType,
      },
    });

    getEndSuggestedLocations({
      variables: {
        riderId: watchRide.riderId,
        transportType: watchTransportType,
      },
    });

    getRiderAvailablePayerTypes({
      variables: {
        riderId: watchRide.riderId,
        accountId: account!.id,
      },
    });
  }, [
    watchRide.riderId,
    getPickupSuggestedLocations,
    getEndSuggestedLocations,
    watchTransportType,
    getRiderAvailablePayerTypes,
    account,
  ]);

  useEffect(() => {
    setAvailablePayerTypes(
      riderAvailablePayerTypesData?.riderAvailablePayerTypes || [],
    );
  }, [riderAvailablePayerTypesData]);

  const notesPlaceholder = useMemo(() => {
    return rideNotesPlaceholderData?.rideNotesPlaceholderText || undefined;
  }, [rideNotesPlaceholderData]);

  const resetMncsOptions = useCallback(() => {
    Object.keys(watchmncsOptions).forEach(option => {
      setValue(`mncsOptions.${option}`, undefined);
    });
  }, [setValue, watchmncsOptions]);

  useEffect(() => {
    if (!watchTransportType.match('ambulance')) {
      resetMncsOptions();
    }
  }, [resetMncsOptions, watchTransportType]);

  const handleGoBack = useCallback(() => {
    setShowMedicalNecessity(false);
  }, []);

  // Whenever we change the rider, cost center or set of available financial
  // responsibility choices, we need to make sure that selected is a subset of
  // those that are available; otherwise nullify it.
  useEffect(() => {
    const selectedResponsibilityChoice =
      accountFinancialResponsibilityChoices?.find(
        (value: FinancialResponsibilityChoice) =>
          value.costCenter === watchRide.costCenter ||
          value.costCenter === defaultRide?.costCenter,
      );
    setRideFinancialResponsibilityChoice(selectedResponsibilityChoice || null);
  }, [
    accountFinancialResponsibilityChoices,
    defaultRide?.costCenter,
    defaultRide?.rider?.id,
    watchRide.costCenter,
    watchRide.riderId,
  ]);

  const [
    getAccountFinancialResponsibility,
    { data: accountFinancialResponsibilityData },
  ] = useAccountFinancialResponsibilityChoicesLazyQuery({
    onCompleted: data => {
      const financialResponsibilityChoices =
        data.accountFinancialResponsibilityChoices as FinancialResponsibilityChoice[];

      setAccountFinancialResponsibilityChoices(financialResponsibilityChoices);

      if (watchRide?.costCenter) {
        return;
      }

      if (financialResponsibilityChoices?.length === 1) {
        setValue(
          'ride.costCenter',
          financialResponsibilityChoices[0].costCenter,
        );
        setSingleCostCenterStyle('none');
      } else if (isEmpty(financialResponsibilityChoices)) {
        setValue('ride.costCenter', null);
        setValue('ride.payerType', null);
      } else {
        setSingleCostCenterStyle('inline-flex');
      }
    },
  });

  const [getRider, { data: riderData }] = useRiderProfileQueryLazyQuery({
    onCompleted: ({ rider }) => {
      setCurrentRider(rider as Rider);
      setPaymentDataFetchedAt(new Date());
    },
  });

  const refreshCurrentRiderData = async () => {
    return getRider({ variables: { riderId: watchRide.riderId } });
  };

  useEffect(() => {
    if (watchRide.payerType) {
      getAccountFinancialResponsibility({
        variables: {
          accountId: account!.id,
          payerType: watchRide.payerType,
          riderId: watchRide.riderId,
        },
      });
    } else if (defaultRide?.payerType) {
      getAccountFinancialResponsibility({
        variables: {
          accountId: account!.id,
          payerType: defaultRide?.payerType,
          riderId: watchRide.riderId,
        },
      });
    }
  }, [
    account,
    watchRide.payerType,
    watchRide.costCenter,
    defaultRide?.payerType,
    watchRide.riderId,
    getAccountFinancialResponsibility,
  ]);

  useEffect(() => {
    if (watchRide.heightCm === '' || watchRide.heightCm == null) {
      setValue('ride.heightInches', '');
      return;
    }
    const heightInchesFromCm = watchRide.heightCm * INCHES_PER_CM;
    const heightInch = Math.floor(heightInchesFromCm % 12);
    const heightFeet = Math.trunc(heightInchesFromCm / 12);

    setValue('ride.heightInch', heightInch);
    setValue('ride.heightFeet', heightFeet);
    setValue('ride.heightInches', heightInchesFromCm);
    if (heightInchesFromCm > 0) {
      trigger('ride.heightInches');
    }
  }, [watchRide.heightCm, setValue, trigger]);

  const submit = async (data: RideFormTypeSubmit) => {
    const { mncsOptions: mncsOptionsData, ...rest } = data;
    const mncsOptions: Array<CreateMncsInput> = mncsOptionsData
      ? Object.keys(mncsOptionsData)
          .filter(key => !!mncsOptionsData[key])
          .map(key => {
            return {
              slug: key,
              value: mncsOptionsData[key].toString(),
            };
          })
      : [];

    // TODO REFACTOR: Instead of using useState and adding it here, use it inside form fields
    const restData = { ...rest };
    restData.ride.arrivalBufferMinutes = bufferTime;
    restData.ride.startLocationPhoto = showStartLocationPhoto
      ? restData.ride.startLocation?.locationPhoto || null
      : null;
    restData.ride.endLocationPhoto = showEndLocationPhoto
      ? restData.ride.endLocation?.locationPhoto || null
      : null;

    formValuesRef.current = { ...restData, mncsOptions };

    if (shouldShowFulfillmentNoticeRef.current) {
      setShouldShowFulfillmentNotice(true);
      return;
    }

    await onSubmit({ ...restData, mncsOptions });

    // Reset the location photo fields.
    setShowStartLocationPhoto(true);
    setShowEndLocationPhoto(true);
  };

  const shouldShowFulfillmentNoticeRef = useRef(false);
  const formValuesRef = useRef<RideFormType | null>(null);

  const handleDOWChange = (checked: boolean, index: number) => {
    const {
      recurringOptions: { dow },
    } = getValues();
    if (!dow) {
      return [index];
    }
    const indexValue = dow.findIndex(val => val === index);
    if (indexValue !== -1) {
      dow[indexValue] = checked ? index : -1;
      const newSelectedDow = dow.filter(value => value !== -1).sort();
      return [...newSelectedDow];
    } else {
      const selectedValue = [...dow, index];
      return [...selectedValue].sort();
    }
  };

  const updateRidePaymentMethod = (id: number | string | null) => {
    register('ride.paymentMethodId', id); // registering it here since otherwise field is not attached on first render
    setValue('ride.paymentMethodId', id);
  };

  useEffect(() => {
    if (defaultRide?.rider?.id) {
      const rider = ridersList?.find(r => r.id === defaultRide?.rider?.id);
      setCurrentRider(rider || null);
    } else if (watchRide.riderId) {
      const rider = ridersList?.find(r => r.id === watchRide.riderId);
      setCurrentRider(rider || null);
    } else {
      setCurrentRider(null);
    }
  }, [watchRide.riderId, defaultRide?.rider?.id, setCurrentRider]);

  const [
    displayPayerDemographicForm,
    displayPaymentMethodForm,
    availablePaymentMethods,
    currentPayer,
  ] = usePaymentMethods(
    account,
    currentRider,
    watchRide?.payerType,
    rideFinancialResponsibilityChoice,
    paymentDataFetchedAt,
  );

  useEffect(() => {
    if (availablePaymentMethods.length) {
      updateRidePaymentMethod(availablePaymentMethods[0]?.id);
    } else {
      updateRidePaymentMethod(null);
    }
  }, [paymentDataFetchedAt, availablePaymentMethods]);

  useEffect(() => {
    if (
      availablePaymentMethods?.length > 0 &&
      !defaultRide?.paymentMethod?.id
    ) {
      const currentSelectedPaymentMethod = availablePaymentMethods.find(
        pm => pm.id === getValues().ride.paymentMethodId,
      );

      if (currentSelectedPaymentMethod?.id) {
        setDefaultPaymentMethodId(currentSelectedPaymentMethod.id);
      } else {
        setDefaultPaymentMethodId(availablePaymentMethods[0].id);
      }
    } else {
      if (defaultRide?.paymentMethod?.id) {
        setDefaultPaymentMethodId(defaultRide?.paymentMethod?.id);
      } else {
        setDefaultPaymentMethodId(null);
      }
    }
  }, [availablePaymentMethods, defaultRide?.paymentMethod?.id, getValues]);

  // TODO: this is a code duplication, but in this context there is aditional code that has some logic
  // that I could not find the use case, leaving this as it is for now to not break anything
  useEffect(() => {
    const rider = ridersList?.find(r => r.id === riderId);

    if (rider && riderId) {
      if (displayPayerDemographicForm && !displayPaymentMethodForm) {
        setValue('ride.payer.firstName', rider?.firstName);
        setValue('ride.payer.lastName', rider?.lastName);
        setValue('ride.payer.phone', rider?.phone);
        setValue('ride.payer.email', rider?.email);
      }

      /**
       * The persisted flow is temporarily disabled to prevent data inconsistencies.
       */
      // const transportationForm = readSavedFormData();

      // if (transportationForm) {
      //   transportationForm?.startTime &&
      //     setValue('startTime', transportationForm.startTime);
      //   transportationForm?.endTime &&
      //     setValue('endTime', transportationForm.endTime);
      //   Object.entries(transportationForm).forEach(([key, value]) => {
      //     if (value) {
      //       if (key === 'requestedStartTime') {
      //         setValue(`ride.${key}`, new Date(value));
      //       } else if (key === 'riderId') {
      //         setValue(`ride.${key}`, riderId);
      //       } else {
      //         setValue(`ride.${key}`, value);
      //       }
      //     }
      //   });
      // }
    }
  }, [
    riderId,
    profile,
    displayPayerDemographicForm,
    displayPaymentMethodForm,
    setValue,
    ridersList,
    defaultRide,
  ]);

  useEffect(() => {
    if (showRecurringOptions) {
      setValue('ride.firstAvailable', false);
    }

    if (watchRide.firstAvailable) {
      setValue('ride.rideType', 'oneway');
    }
  }, [setValue, watchRide.firstAvailable, showRecurringOptions]);

  const errorPayerType = !!errors.ride?.payerType;
  const errorCostCenter = !!errors.ride?.costCenter;

  const debouncePickupSuggestedLocationSearch = debounce(
    getPickupSuggestedLocations,
    300,
  );
  const debounceEndSuggestedLocationSearch = debounce(
    getEndSuggestedLocations,
    300,
  );

  const getPickupLocationSearch = useCallback(
    (query?: string) => {
      if (query) {
        debouncePickupSuggestedLocationSearch({
          variables: {
            riderId: watchRide.riderId,
            transportType: watchTransportType,
            query,
          },
        });
      } else if (query === '') {
        debouncePickupSuggestedLocationSearch({
          variables: {
            riderId: watchRide.riderId,
            transportType: watchTransportType,
            query: '',
          },
        });
      }
    },
    [debouncePickupSuggestedLocationSearch, watchRide, watchTransportType],
  );

  const getEndLocationSearch = useCallback(
    (query?: string) => {
      if (query) {
        debounceEndSuggestedLocationSearch({
          variables: {
            riderId: watchRide.riderId,
            transportType: watchTransportType,
            query,
          },
        });
      } else if (query === '') {
        debounceEndSuggestedLocationSearch({
          variables: {
            riderId: watchRide.riderId,
            transportType: watchTransportType,
            query: '',
          },
        });
      }
    },
    [debounceEndSuggestedLocationSearch, watchRide.riderId, watchTransportType],
  );

  const selectedRider = useMemo(() => {
    return ridersList?.find(r => r.id === watchRide.riderId);
  }, [ridersList, watchRide]);

  useEffect(() => {
    if (selectedRider) {
      // Set rider's weight and height when available
      if (!getValues('ride.weight')) {
        setValue(
          'ride.weight',
          useMetric
            ? getKgFromLBS(selectedRider.weightLbs || 0)
            : selectedRider.weightLbs,
        );
      }

      if (!getValues('ride.heightInches')) {
        const inches = selectedRider.heightInches || 0;

        setValue('ride.heightInches', inches);

        const heightInch = Math.floor(inches % 12);
        const heightFeet = Math.trunc(inches / 12);

        setValue('ride.heightInch', heightInch);
        setValue('ride.heightFeet', heightFeet);

        setValue(
          'ride.heightCm',
          useMetric ? getCmFromInches(inches) : getValues('ride.heightCm'),
        );
      }

      // Set available transport types based on rider's mobility assistance
      // Wheelchair-bound riders can only use wheelchair-bound transport types
      if (
        selectedRider.mobilityAssistance === WHEELCHAIR_BOUND_RIDER_MOBILITY
      ) {
        setAvailableTransportTypes(wheelchairBoundTransportTypes);

        // Reset the currently selected transport type if it is not valid for wheelchair-bound riders
        if (
          !WHEELCHAIR_BOUND_TRANSPORT_TYPES.includes(watchRide.transportType)
        ) {
          const defaultWheelchairBoundTransportTypeName =
            wheelchairBoundTransportTypes.length > 0
              ? wheelchairBoundTransportTypes[0].name
              : '';
          setValue(
            'ride.transportType',
            defaultWheelchairBoundTransportTypeName,
          );
        }
      } else {
        setAvailableTransportTypes(allTransportTypes);
      }
    } else {
      setAvailableTransportTypes(allTransportTypes);
    }
  }, [
    selectedRider,
    useMetric,
    watchTransportType,
    allTransportTypes,
    wheelchairBoundTransportTypes,
    setValue,
    getValues,
    watchRide.transportType,
  ]);

  const [validateSmsCapability, { data: dataPhoneInfo }] =
    useValidateSmsCapabilityLazyQuery();

  const [validateLocationGeo] = useValidateLocationGeoMutationMutation();

  const handleLocationGeoValidation = useCallback(
    async (
      location: any,
      setWarning: (msg: string | null) => void,
      lastValidatedCoords: React.MutableRefObject<{ latitude: number; longitude: number } | null>
    ) => {
      if (!location?.latitude || !location?.longitude) {
        setWarning(null);
        lastValidatedCoords.current = null;
        return;
      }

      // Skip if we already validated these exact coordinates to prevent two requests in a row
      // when a location changes because latitude and longitude are being watched separately
      if (
        lastValidatedCoords.current?.latitude === location.latitude &&
        lastValidatedCoords.current?.longitude === location.longitude
      ) {
        return;
      }

      lastValidatedCoords.current = {
        latitude: location.latitude,
        longitude: location.longitude,
      };
      setWarning(null);

      const result = await validateLocationGeo({
        variables: {
          latitude: location.latitude,
          longitude: location.longitude,
        },
      });

      const errorMessage = result.data?.validateLocationGeo?.error;
      if (
        errorMessage &&
        errorMessage.startsWith(
          'This address is outside of the designated service area.',
        )
      ) {
        // Though the backend returns this as an error, we want to show it as a warning in the form
        setWarning(errorMessage);
      }
    },
    [validateLocationGeo],
  );

  // Verify initial locations when component loads or locations are set
  useEffect(() => {
    if (watchRide.startLocation?.latitude && watchRide.startLocation?.longitude) {
      handleLocationGeoValidation(
        watchRide.startLocation,
        setStartLocationGeoWarning,
        lastValidatedStartCoords,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchRide.startLocation?.latitude, watchRide.startLocation?.longitude]);

  useEffect(() => {
    if (watchRide.endLocation?.latitude && watchRide.endLocation?.longitude) {
      handleLocationGeoValidation(
        watchRide.endLocation,
        setEndLocationGeoWarning,
        lastValidatedEndCoords,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchRide.endLocation?.latitude, watchRide.endLocation?.longitude]);

  const paymentMethodErrorMessage = () => {
    if (errors?.ride?.paymentMethodId) {
      // The `message` prop for this error is an empty string when the `type` is required.
      return errors.ride.paymentMethodId.type === 'required'
        ? 'Payment Method is required'
        : errors.ride.paymentMethodId.message;
    }

    return '';
  };

  shouldShowFulfillmentNoticeRef.current =
    !isRideshare &&
    Math.abs(differenceInMinutes(new Date(), watchRide.requestedStartTime)) <
      REQUESTED_START_GAP_MINUTES;

  const isMobile = dataPhoneInfo?.validateSmsCapability?.isMobile;
  const isUnsubscribedSms =
    dataPhoneInfo?.validateSmsCapability?.isUnsubscribedSms;

  const splitPayConfigInvalid = () => {
    if (watchRide?.payerType === SPLIT_PAY_PAYER_TYPE && !account?.invoiced) {
      setError('ride.payerType', {
        message: 'Only available for accounts that can be invoiced',
        type: 'custom',
      });
      return true;
    }

    clearErrors('ride.payerType');
    return false;
  };

  const disableSubmit = useMemo(() => {
    return (
      (isRideshare && selectedRider && !validPhone(selectedRider?.phone)) ||
      splitPayConfigInvalid()
    );
  }, [selectedRider, watchTransportType, watchRide?.payerType]);

  const transportTypeOptions = useMemo(() => {
    return {
      contactPrecautionsRequired:
        !!transportTypeOptionsData?.rideTransportTypeOptions
          .contactPrecautionsRequired,
      oxygenRequired:
        !!transportTypeOptionsData?.rideTransportTypeOptions.oxygenRequired,
    };
  }, [actionType, transportTypeOptionsData, defaultRide]);

  const mncsOptionsErrorPresent = useMemo(() => {
    let mncsOptionsErrorsExist = false;
    Object.keys(errors).forEach(key => {
      if (key.includes('mncsOptions')) {
        mncsOptionsErrorsExist = true;
      }
    });
    return mncsOptionsErrorsExist;
  }, [Object.keys(errors).length]);

  const MapElement = useMemo(() => {
    if (isVetride) {
      return null;
    }
    return (
      <Grid item key="map" xs={false} sm={5} md={6} lg={8}>
        <Map
          // @ts-ignore
          center={{
            latitude: profile?.lastGeoLatitude || DEFAULT_LATITUDE,
            longitude: profile?.lastGeoLongitude || DEFAULT_LONGITUDE,
          }}
          startLocation={watchRide.startLocation}
          endLocation={watchRide.endLocation}
          startTime={
            !watchRide.flexibleRide && requestedStartTime
              ? convertLocalToISOInTimeZone(requestedStartTime, timeZone)
              : null
          }
          endTime={
            requestedEndTime
              ? convertLocalToISOInTimeZone(requestedEndTime, timeZone)
              : null
          }
          tzName={timeZone}
        />
      </Grid>
    );
  }, [
    isVetride,
    profile,
    watchRide,
    requestedStartTime,
    requestedEndTime,
    defaultRide,
    timeZone,
  ]);

  const TransportReasonCategoriesComponent = useMemo(() => {
    return watchRide.transportReason || isCreate ? (
      <TransportReasonCategories
        control={control}
        transportReasonErrors={errors.ride?.transportReason}
        transportReason={watchRide.transportReason}
        transportReasonCommentErrors={errors.ride?.transportReasonComment}
        transportReasonDayErrors={
          errors.appointmentTimeTransportReason?.startAppointmentDay
        }
        transportReasonTimeErrors={
          errors.appointmentTimeTransportReason?.startAppointmentTime
        }
      />
    ) : null;
  }, [
    watchRide.transportReason,
    actionType,
    errors.ride?.transportReasonComment,
    errors.ride?.transportReason,
    errors.appointmentTimeTransportReason?.startAppointmentDay,
    errors.appointmentTimeTransportReason?.startAppointmentTime,
    control,
  ]);

  const MnscLink = () => (
    <>
      <InputLabel
        className={classes.formControl}
        id="medical-necessity-statement-label"
      >
        Medical Necessity Statement
      </InputLabel>

      <Button
        style={{ color: mncsOptionsErrorPresent ? 'red' : '' }}
        color={'primary'}
        variant={mncsOptionsErrorPresent ? 'outlined' : 'text'}
        onClick={() => setShowMedicalNecessity(true)}
        className={classes.addInstructionText}
      >
        {isUpdate
          ? 'Medical Necessity Statement'
          : 'Add Medical Necessity Statement'}
      </Button>
    </>
  );

  const setUseMetricCb = (isMetric: boolean) => {
    setUseMetric(isMetric);

    setValue(
      'ride.weight',
      isMetric
        ? getKgFromLBS(watchRide.weight)
        : getLBSFromKg(watchRide.weight),
    );

    setValue(
      'ride.heightCm',
      isMetric ? getCmFromInches(watchRide.heightInches) : watchRide.heightCm,
    );
  };

  const isReturnTripAvailable = !isVetride && !isReturnTrip;
  const isRecurringTripAvailable = !isVetride && isCreate;

  const handlePickupTimeSubmit = (
    arrivalBufferMinutes: number,
    time: string,
    date: Date | null,
  ) => {
    if (time && date) {
      setValue('ride.requestedStartTime', date);
      setRequestedStartTime(date);
      setValue('startTime', time);
      setBufferTime(arrivalBufferMinutes);
    }
  };

  useEffect(() => {
    if (defaultRide?.arrivalBufferMinutes !== null) {
      setBufferTime(defaultRide?.arrivalBufferMinutes);
    }
  }, [defaultRide?.arrivalBufferMinutes]);

  useEffect(() => {
    if (isRideshare && selectedRider?.phone) {
      validateSmsCapability({
        variables: { phoneNumber: selectedRider.phone },
      });
    }
  }, [selectedRider, validateSmsCapability, isRideshare]);

  return (
    <Box display="flex" justifyContent="center" flexGrow={1}>
      <AddMedicalNecessityStatement
        control={control}
        onGoBack={handleGoBack}
        mncsOptions={watchmncsOptions}
        errors={errors}
        trigger={trigger}
        watchTransportType={watchTransportType}
        showMedicalNecessity={showMedicalNecessity}
      />
      <Grid
        style={{
          display: showMedicalNecessity ? 'none' : 'flex',
          justifyContent: isVetride ? 'center' : 'normal',
        }}
        container
        wrap="nowrap"
      >
        <Grid
          item
          xs={12}
          sm={isVetride ? 12 : 7}
          md={isVetride ? 12 : 6}
          lg={isVetride ? 12 : 5}
          key="createForm"
          className={classes.leftGrid}
        >
          <Paper className={classes.left}>
            {showSessionInfo && <SessionInformation />}

            {isVetride === false && (
              <Button
                color="primary"
                startIcon={<ArrowBackIcon />}
                onClick={() => history.goBack()}
              >
                Back
              </Button>
            )}

            <Typography component="h1" variant="h5">
              {isUpdate ? 'Update Transportation' : 'Book Transportation'}
            </Typography>

            {createError && <Alert severity="error">{createError} </Alert>}

            <form className={classes.form} noValidate onSubmit={onFormSubmit}>
              <RiderInputComponent
                control={control}
                isUpdate={isUpdate}
                isEhrPatientContext={isEhrPatientContext}
                ridersList={ridersList}
                defaultRide={defaultRide}
                errors={errors}
                isRideshare={isRideshare}
                selectedRider={selectedRider}
                watchRide={watchRide}
                watchTransportType={watchTransportType}
                isCreate={isCreate}
                setRidersList={setRidersList}
                riderPhoneInfo={dataPhoneInfo?.validateSmsCapability}
              />
              <TransportTypeSelector
                control={control}
                transportTypeErrors={errors.ride?.transportType}
                watchRide={watchRide}
                transportTypes={availableTransportTypes}
                disabled={isUpdate}
              />
              <TransportTypeOptions
                actionType={actionType}
                control={control}
                errorWeight={errors.ride?.weight}
                errorHeightInches={errors.ride?.heightInches}
                transportType={watchTransportType}
                watchRide={watchRide}
                useMetric={useMetric}
                setUseMetric={setUseMetricCb}
                stairsCount={watchRide.stairsCount}
              />

              {TransportReasonCategoriesComponent}

              {transportTypeOptionsData?.rideTransportTypeOptions
                .promptForBedsideNurseContact && (
                <BedSideNurseComponent
                  control={control}
                  disabled={false}
                  errors={errors}
                />
              )}
              <TransportTypeFeeOptionsComponent
                control={control}
                disabled={false}
                options={transportTypeOptions}
              />
              {!isUpdate &&
                profile?.promptForFinancialResponsibility &&
                availablePayerTypes.length > 0 && (
                  <>
                    <FinancialResponsibilityForm
                      control={control}
                      account={account}
                      errors={errors}
                      profile={{
                        ...profile,
                        availablePayerTypes: availablePayerTypes,
                      }}
                      getAccountFinancialResponsibility={
                        getAccountFinancialResponsibility
                      }
                      watchTransportType={watchTransportType}
                      accountFinancialResponsibilityChoices={
                        accountFinancialResponsibilityChoices
                      }
                      errorPayerType={errorPayerType}
                      errorCostCenter={errorCostCenter}
                      singleCostCenterStyle={singleCostCenterStyle}
                      setValue={setValue}
                      promptForPayerDemographics={displayPayerDemographicForm}
                      requirePaymentFromPrivatePayerOnBooking={
                        displayPaymentMethodForm
                      }
                      ridersList={ridersList}
                      defaultRider={defaultRider}
                      defaultRide={defaultRide}
                      watchRide={watchRide}
                      riderID={riderId ?? null}
                    />
                  </>
                )}
              {transportTypeOptionsData?.rideTransportTypeOptions
                .promptForMncs && <MnscLink />}
              <FormControl
                className={classes.formControl}
                error={!!errors.ride?.rideType}
              >
                <InputLabel id="select-type-label">Ride Type</InputLabel>

                <Controller
                  control={control}
                  name="ride.rideType"
                  rules={{ required: true }}
                  as={
                    <Select labelId="select-type-label" id="rideTypeSelect">
                      {filteredRideTypes.map(rideType => (
                        <MenuItem
                          disabled={
                            watchRide.firstAvailable &&
                            rideType.type !== 'oneway'
                          }
                          key={rideType.type}
                          value={rideType.type}
                        >
                          {rideType.text}
                        </MenuItem>
                      ))}
                    </Select>
                  }
                />
                {errors.ride?.rideType && (
                  <FormHelperText>Select a ride type</FormHelperText>
                )}
              </FormControl>
              {isRideshare && (
                <RideshareComponents
                  control={control}
                  isUpdate={isUpdate}
                  showRecurringOptions={showRecurringOptions}
                  invalidPhone={
                    !isMobile ||
                    isUnsubscribedSms ||
                    !validPhone(selectedRider?.phone)
                  }
                />
              )}

              <Controller
                control={control}
                name="ride.startLocation"
                rules={{ required: true, validate: trueAddress }}
                onChange={([, data]: any) => data}
                render={({ onChange, ...props }) => {
                  return (
                    <GooglePlacesAutocomplete
                      center={{
                        latitude:
                          profile?.lastGeoLatitude?.toString() ||
                          DEFAULT_LATITUDE.toString(),
                        longitude:
                          profile?.lastGeoLongitude?.toString() ||
                          DEFAULT_LONGITUDE.toString(),
                      }}
                      id="startLocation"
                      value={props.value}
                      inputStyle={classes.formControl}
                      suggestedLocations={
                        dataPickupSuggestedLocations?.rideSuggestedLocations ||
                        []
                      }
                      label="Pickup Location"
                      inputError={!!errors.ride?.startLocation}
                      onChange={(location: any) => {
                        onChange(location);
                        handleLocationGeoValidation(location, setStartLocationGeoWarning, lastValidatedStartCoords);
                      }}
                      onInputChange={(value: string) => {
                        getPickupLocationSearch(value);
                        if (value === '') {
                          setShowStartLocationPhoto(true);
                        }
                      }}
                      helperText={
                        errors.ride?.startLocation &&
                        ((errors.ride?.startLocation.type === 'required' &&
                          'Pickup location is required') ||
                          (errors.ride?.startLocation.type === 'validate' &&
                            'Pickup location is invalid'))
                      }
                    />
                  );
                }}
              />
              {startLocationGeoWarning && (
                <Alert severity="warning" className={classes.formControl}>
                  {startLocationGeoWarning}
                </Alert>
              )}
              {watchRide.startLocation && watchRide.rideType !== 'delivery' && (
                <>
                  {!watchRide.startLocation.instructions &&
                    !showStartInstructions && (
                      <Box
                        alignSelf="flex-end"
                        display="flex"
                        alignItems="center"
                        className={classes.instructionLink}
                      >
                        <Button
                          color="primary"
                          onClick={() => setShowStartInstructions(true)}
                          className={classes.addInstructionText}
                        >
                          + Add Location Instructions
                        </Button>
                        <TooltipButton title="What should your drivers know about this location? Should they come inside? Parking? Would you like a text or call upon arrival?" />
                      </Box>
                    )}
                  {(watchRide.startLocation.instructions ||
                    showStartInstructions) && (
                    <TextField
                      id="startLocationInstructions"
                      className={classes.formControl}
                      name="ride.startLocationInstructions"
                      defaultValue={watchRide.startLocation.instructions}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label={
                        <LabelWithTooltip
                          labelText="Pickup Location Instructions"
                          tooltipText="Where specifically should the driver meet the rider?"
                        />
                      }
                      multiline
                      maxRows={3}
                      inputRef={register()}
                    />
                  )}
                  {watchRide.startLocation &&
                    watchRide.startLocation.locationPhoto && (
                      <div className={classes.locationPhoto}>
                        <LocationPhoto
                          locationPhoto={watchRide.startLocation.locationPhoto}
                          isDeleted={!showStartLocationPhoto}
                          onDeletePhoto={() => setShowStartLocationPhoto(false)}
                        />
                      </div>
                    )}
                </>
              )}
              {!isRideshare && profile?.promptForRoomNumbers && (
                <Grid container className={classes.formControl}>
                  <Grid item xs={12}>
                    <Controller
                      name="ride.pickupRoomNumber"
                      control={control}
                      render={props => {
                        return (
                          <TextField
                            value={props.value || ''}
                            fullWidth
                            label="Pickup Room Number"
                            onChange={e => {
                              if (e.target.value.length <= 10) {
                                props.onChange(e.target.value);
                              }
                            }}
                          />
                        );
                      }}
                    />
                  </Grid>
                </Grid>
              )}
              <Controller
                control={control}
                name="ride.endLocation"
                rules={{ required: true, validate: trueAddress }}
                render={({ onChange, ...props }) => {
                  return (
                    <GooglePlacesAutocomplete
                      center={{
                        latitude:
                          profile?.lastGeoLatitude?.toString() ||
                          DEFAULT_LATITUDE.toString(),
                        longitude:
                          profile?.lastGeoLongitude?.toString() ||
                          DEFAULT_LONGITUDE.toString(),
                      }}
                      value={props.value}
                      suggestedLocations={
                        dataEndSuggestedLocations?.rideSuggestedLocations || []
                      }
                      id="endLocation"
                      inputStyle={classes.formControl}
                      label="Dropoff Location"
                      inputError={!!errors.ride?.endLocation}
                      onChange={(location: any) => {
                        onChange(location);
                        handleLocationGeoValidation(location, setEndLocationGeoWarning, lastValidatedEndCoords);
                      }}
                      onInputChange={(value: string) => {
                        if (value === '') {
                          setShowEndLocationPhoto(true);
                        }
                        getEndLocationSearch(value);
                      }}
                      helperText={
                        errors.ride?.endLocation &&
                        ((errors.ride?.endLocation.type === 'required' &&
                          'Dropoff location is required') ||
                          (errors.ride?.endLocation.type === 'validate' &&
                            'Dropoff location is invalid'))
                      }
                    />
                  );
                }}
              />
              {endLocationGeoWarning && (
                <Alert severity="warning" className={classes.formControl}>
                  {endLocationGeoWarning}
                </Alert>
              )}
              {watchRide.endLocation && (
                <>
                  {!watchRide.endLocation.instructions &&
                    !showEndInstructions && (
                      <Box
                        alignSelf="flex-end"
                        display="flex"
                        alignItems="center"
                        className={classes.instructionLink}
                      >
                        <Button
                          color="primary"
                          onClick={() => setShowEndInstructions(true)}
                          className={classes.addInstructionText}
                        >
                          + Add Location Instructions
                        </Button>
                        <TooltipButton
                          title={
                            watchRide.rideType === 'delivery'
                              ? 'The driver will drop your items at the door.  What should your driver know about this location? Parking? Would you like a text or call upon arrival? '
                              : 'What should your drivers know about this location? Should they come inside? Parking? Would you like a text or call upon arrival?'
                          }
                        />
                      </Box>
                    )}
                  {(watchRide.endLocation.instructions ||
                    showEndInstructions) && (
                    <TextField
                      id="endLocationInstructions"
                      className={classes.formControl}
                      name="ride.endLocationInstructions"
                      defaultValue={watchRide.endLocation.instructions}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label={
                        <LabelWithTooltip
                          labelText="Dropoff Location Instructions"
                          tooltipText="Where specifically should the driver park and take the rider?"
                        />
                      }
                      multiline
                      maxRows={3}
                      inputRef={register()}
                    />
                  )}
                  {watchRide.endLocation &&
                    watchRide.endLocation.locationPhoto && (
                      <div className={classes.locationPhoto}>
                        <LocationPhoto
                          locationPhoto={watchRide.endLocation.locationPhoto}
                          isDeleted={!showEndLocationPhoto}
                          onDeletePhoto={() => setShowEndLocationPhoto(false)}
                        />
                      </div>
                    )}
                </>
              )}
              {!isRideshare && profile?.promptForRoomNumbers && (
                <Grid container className={classes.formControl}>
                  <Grid item xs={12}>
                    <Controller
                      name="ride.dropoffRoomNumber"
                      control={control}
                      render={props => {
                        return (
                          <TextField
                            value={props.value || ''}
                            fullWidth
                            label="Dropoff Room Number"
                            onChange={e => {
                              if (e.target.value.length <= 10) {
                                props.onChange(e.target.value);
                              }
                            }}
                          />
                        );
                      }}
                    />
                  </Grid>
                </Grid>
              )}

              <RideTime
                control={control}
                defaultValues={defaultValues}
                firstAvailable={watchRide.firstAvailable}
                flexibleRide={watchRide.flexibleRide}
                rideType={watchRide.rideType}
                requestedStartTime={watchRide.requestedStartTime}
                startTime={watchStartTime}
                endTime={watchEndTime}
                startLocation={watchRide.startLocation}
                endLocation={watchRide.endLocation}
                bufferTime={bufferTime}
                errors={errors}
                pickupTimeUpdated={handlePickupTimeSubmit}
              />

              {watchRide.rideType !== 'delivery' && (
                <FormControl className={classes.formControl}>
                  <InputLabel id="select-passenger-count-label">
                    Passenger Count
                  </InputLabel>

                  <Controller
                    control={control}
                    name="ride.passengerCount"
                    rules={{ required: true }}
                    as={
                      <Select
                        labelId="select-passenger-count-label"
                        id="passengerCountSelect"
                      >
                        <MenuItem key={1} value={1}>
                          1
                        </MenuItem>
                        <MenuItem key={2} value={2}>
                          2
                        </MenuItem>
                        <MenuItem key={3} value={3}>
                          3
                        </MenuItem>
                        <MenuItem key={4} value={4}>
                          4
                        </MenuItem>
                      </Select>
                    }
                  />
                </FormControl>
              )}
              <TextField
                id="notes"
                className={classes.formControl}
                name="ride.notes"
                InputLabelProps={{
                  shrink: true,
                }}
                label={
                  <LabelWithTooltip
                    labelText={
                      watchRide.rideType === 'delivery'
                        ? 'Delivery Details'
                        : 'Ride Notes'
                    }
                    tooltipText={
                      watchRide.rideType === 'delivery'
                        ? 'Please describe the items you would like and any other details about your delivery. You will be able to update this later.'
                        : `Please include any special needs, precautions, medications, or anything else the driver should know about this rider and transport. ${
                            notesPlaceholder || ''
                          }`
                    }
                  />
                }
                placeholder={notesPlaceholder}
                multiline
                minRows={4}
                inputRef={register()}
              />
              {watchRide.rideType === 'delivery' && (
                <FormControl
                  component="fieldset"
                  className={classes.formControl}
                >
                  <FormControlLabel
                    label={
                      <LabelWithTooltip
                        labelText="Allow Substitutions"
                        tooltipText="After unsuccessfully trying to contact you should your driver make substitutions?"
                      />
                    }
                    control={
                      <Controller
                        name="ride.deliverySubstitution"
                        control={control}
                        render={props => (
                          <Switch
                            onChange={e =>
                              props.onChange(e.target.checked ? 'yes' : 'no')
                            }
                            checked={!!props.value}
                          />
                        )}
                      />
                    }
                  />
                </FormControl>
              )}
              {availablePaymentMethods.length <= 1 &&
                (!profile?.promptForFinancialResponsibility ||
                  displayPaymentMethodForm) && (
                  <FormControl
                    error={!!errors.ride?.paymentMethodId}
                    className={classes.block}
                  >
                    <Controller
                      defaultValue={watchRide.paymentMethodId} //required to explicitly set default with watch
                      as={<input />}
                      rules={{ required: true }}
                      type="hidden"
                      name="ride.paymentMethodId"
                      control={control}
                    />

                    {errors.ride?.paymentMethodId && (
                      <FormHelperText>
                        A payment method is required
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              {!!watchRide.riderId &&
                watchRide.startLocation &&
                watchRide.endLocation &&
                requestedStartTime &&
                (watchRide.rideType !== 'roundtrip' || requestedEndTime) && (
                  <Box className={classes.fareSection}>
                    {availablePaymentMethods.length === 0 &&
                      !displayPayerDemographicForm && (
                        <Box textAlign="center">
                          <Box
                            className={classes.addPaymentText}
                            fontStyle="italic"
                            fontWeight="fontWeightMedium"
                          >
                            To book a ride, you first need to add a payment
                            method
                          </Box>

                          <span>
                            <Button
                              onClick={handleClick}
                              // className={classes.actionButton}
                              color="primary"
                              startIcon={<AddIcon />}
                            >
                              Add Payment Method
                            </Button>
                          </span>

                          {stripePromise && stripeOptions && (
                            <div className={classes.cardStyle}>
                              <Elements
                                stripe={stripePromise}
                                options={stripeOptions}
                              >
                                {currentPayer?.type ===
                                  CurrentPayerType.Account && (
                                  <StripeForm
                                    shouldShowAddButton={true}
                                    stripeData={stripeData}
                                    getSetupIntent={getSetupIntent}
                                    getAccount={getAccountData}
                                    ownerType={'Account'}
                                  />
                                )}

                                {currentPayer?.type ===
                                  CurrentPayerType.Rider && (
                                  <StripeForm
                                    shouldShowAddButton={true}
                                    rider={currentRider}
                                    stripeData={stripeData}
                                    getSetupIntent={getSetupIntent}
                                    getAccount={refreshCurrentRiderData}
                                    ownerType={'Rider'}
                                  />
                                )}
                              </Elements>
                            </div>
                          )}
                        </Box>
                      )}
                    {availablePaymentMethods.length === 1 &&
                    showInvoiceRideLabel &&
                    !displayPayerDemographicForm ? (
                      <InvoiceRide
                        paymentMethodOptions={availablePaymentMethods}
                      />
                    ) : (
                      <></>
                    )}
                    {availablePaymentMethods.length > 1 && (
                      <FormControl
                        className={classes.formControl}
                        error={!!errors.ride?.paymentMethodId}
                      >
                        <InputLabel id="select-payment-method-label">
                          Payment Method
                        </InputLabel>

                        <Controller
                          control={control}
                          name="ride.paymentMethodId"
                          defaultValue={watchRide.paymentMethodId || ''} //required to explicitly set default with watch
                          rules={{ required: true }}
                          as={
                            <Select
                              labelId="select-payment-method-label"
                              id="paymentMethodSelect"
                            >
                              {availablePaymentMethods.map(pm => (
                                <MenuItem key={pm.id} value={pm.id}>
                                  {pm.name}
                                </MenuItem>
                              ))}
                            </Select>
                          }
                        />
                        {errors.ride?.paymentMethodId && (
                          <FormHelperText error>
                            {paymentMethodErrorMessage()}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                    <Box className={classes.estimate}>
                      <RideEstimate
                        isFlexibleRide={watchRide.flexibleRide}
                        rideType={watchRide.rideType}
                        startLocation={watchRide.startLocation}
                        endLocation={watchRide.endLocation}
                        requestedStartTime={requestedStartTime}
                        requestedEndTime={requestedEndTime}
                        payerType={watchRide.payerType}
                        costCenter={watchRide.costCenter}
                        transportType={watchTransportType}
                        weightLbs={
                          isMetric
                            ? getLBSFromKg(watchRide.weight)
                            : watchRide.weight
                        }
                        stairsCount={watchRide.stairsCount}
                        oxygenRequired={watchRide.oxygenRequired}
                        contactPrecautionsRequired={
                          watchRide.contactPrecautionsRequired
                        }
                        arrivalBufferMinutes={bufferTime}
                        paymentMethodOptions={availablePaymentMethods}
                        paymentMethodId={watchRide.paymentMethodId}
                        callbackRideEstimate={rideEstimate => {
                          if (
                            showInvoiceRideLabel &&
                            rideEstimate.splitPayerEstimates &&
                            rideEstimate.splitPayerEstimates.length > 0
                          ) {
                            setShowInvoiceRideLabel(false);
                          }
                        }}
                      />
                    </Box>
                  </Box>
                )}
              {isAdmin && !isUpdate && (
                <FormControl
                  component="fieldset"
                  className={classes.formControl}
                >
                  <FormControlLabel
                    label={
                      <LabelWithTooltip
                        labelText="Draft"
                        tooltipText="(only admins see this)"
                      />
                    }
                    control={
                      <Controller
                        name="ride.draft"
                        control={control}
                        render={props => (
                          <Switch
                            onChange={e => props.onChange(e.target.checked)}
                            checked={!!props.value}
                          />
                        )}
                      />
                    }
                  />
                </FormControl>
              )}
              {isRecurringTripAvailable && (
                <FormControl
                  component="fieldset"
                  className={classes.formControl}
                  disabled={watchRide.firstAvailable}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showRecurringOptions}
                        onChange={toggleRecurringOptions}
                      />
                    }
                    label={
                      <LabelWithTooltip
                        labelText={'Recurring Trip'}
                        tooltipText={
                          'Check this box to create a repeating trip based on a schedule'
                        }
                      />
                    }
                  />
                </FormControl>
              )}
              {showRecurringOptions && (
                <Box className={classes.fareSection}>
                  <Grid alignItems={'center'} container spacing={2}>
                    <Grid item xs={5}>
                      <Typography style={{ paddingTop: 15 }} variant="body1">
                        Repeat Every
                      </Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <FormControl
                        component="fieldset"
                        className={classes.formControl}
                      >
                        <Controller
                          name="recurringOptions.interval"
                          control={control}
                          min={1}
                          max={28}
                          defaultValue={watchrecurringOptions.interval} //required to explicitly set default with watch
                          render={props => {
                            return (
                              <Input
                                value={props.value}
                                id="interval"
                                onChange={e => {
                                  if (e.target.value === '') {
                                    props.onChange(e.target.value);
                                  }

                                  if (
                                    !Number.isInteger(
                                      Number.parseInt(e.target.value),
                                    )
                                  ) {
                                    return;
                                  }
                                  if (
                                    +e.target.value < 1 ||
                                    +e.target.value > 28
                                  ) {
                                    return;
                                  } else {
                                    props.onChange(+e.target.value);
                                  }
                                }}
                                type="number"
                              />
                            );
                          }}
                          rules={{ required: true }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={5} className={classes.withTooltip}>
                      <FormControl component="fieldset" style={{ flex: 1 }}>
                        <Controller
                          name="recurringOptions.repeat"
                          control={control}
                          defaultValue={watchrecurringOptions.repeat} //required to explicitly set default with watch
                          render={props => {
                            return (
                              <Select
                                value={props.value}
                                onChange={props.onChange}
                              >
                                <MenuItem value={'weekly'}>Week</MenuItem>
                                <MenuItem value={'daily'}>Day</MenuItem>
                              </Select>
                            );
                          }}
                        />
                      </FormControl>
                      <TooltipButton title="Set your recurrence schedule - either daily or weekly" />
                    </Grid>
                  </Grid>
                  {watchrecurringOptions.repeat === 'weekly' && (
                    <Grid
                      alignItems={'baseline'}
                      container
                      style={{ marginTop: 20 }}
                    >
                      <Grid item xs={12}>
                        <Typography
                          className={
                            errors.recurringOptions?.dow ? classes.root : ''
                          }
                          variant="body1"
                        >
                          Repeat On:
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <FormGroup
                          row
                          style={{
                            flexWrap: 'nowrap',
                          }}
                        >
                          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(
                            (day, index) => (
                              <FormControlLabel
                                style={{ margin: 0 }}
                                labelPlacement="bottom"
                                control={
                                  <FormControl
                                    component="fieldset"
                                    className={classes.formControl}
                                  >
                                    <Controller
                                      name="recurringOptions.dow"
                                      rules={{
                                        validate: e => {
                                          if (!e || e?.length === 0) {
                                            return 'Should be selected at least one day';
                                          }
                                          return true;
                                        },
                                      }}
                                      render={props => {
                                        return (
                                          <Checkbox
                                            className={
                                              errors.recurringOptions?.dow &&
                                              classes.root
                                            }
                                            style={{ padding: 0 }}
                                            defaultChecked={props.value?.includes(
                                              index,
                                            )}
                                            onChange={ev => {
                                              props.onChange(
                                                handleDOWChange(
                                                  ev.target.checked,
                                                  index,
                                                ),
                                              );
                                            }}
                                          />
                                        );
                                      }}
                                      control={control}
                                    />
                                  </FormControl>
                                }
                                key={index}
                                label={
                                  <span
                                    style={
                                      errors.recurringOptions?.dow && {
                                        color: onwardColors.onwardRed,
                                        fontWeight: 'bold',
                                      }
                                    }
                                  >
                                    {day}
                                  </span>
                                }
                              />
                            ),
                          )}
                        </FormGroup>
                        {errors.recurringOptions?.dow && (
                          <FormHelperText style={{ fontSize: 14 }} error>
                            {errors.recurringOptions?.dow.message}
                          </FormHelperText>
                        )}
                      </Grid>
                    </Grid>
                  )}

                  <Grid
                    style={{ marginTop: 10 }}
                    alignItems={'center'}
                    container
                    spacing={2}
                  >
                    <Grid item xs={5}>
                      <Typography style={{ paddingTop: 15 }} variant="body1">
                        Repeat Until:
                      </Typography>
                    </Grid>
                    <Grid item xs={7} className={classes.withTooltip}>
                      <FormControl component="fieldset" style={{ flex: 1 }}>
                        <Controller
                          className={classes.untilDate}
                          control={control}
                          name="recurringOptions.untilDate"
                          rules={{
                            required: true,
                            validate: e => {
                              if (
                                e &&
                                requestedStartTime &&
                                (isSameDay(e, requestedStartTime) ||
                                  isBefore(e, requestedStartTime))
                              ) {
                                return false;
                              }

                              return true;
                            },
                          }}
                          defaultValue={watchrecurringOptions.untilDate}
                          render={({ ref, ...rest }) => {
                            return (
                              <DatePicker
                                id="recurringStartDay"
                                autoOk
                                disablePast
                                minDateMessage="Date should be after Pickup Day"
                                minDate={addDays(
                                  requestedStartTime ?? new Date(),
                                  1,
                                )}
                                maxDate={addMonths(new Date(), 6)}
                                {...rest}
                              />
                            );
                          }}
                        />
                      </FormControl>
                      <TooltipButton title="Set an end date for your schedule. You may schedule rides up to 6 months into the future" />
                    </Grid>
                  </Grid>
                  {isAdmin && (
                    <Grid container>
                      <Grid item xs={12} className={classes.withTooltip}>
                        <FormControl component="fieldset" style={{ flex: 1 }}>
                          <Controller
                            name="recurringOptions.notes"
                            control={control}
                            render={props => {
                              return (
                                <TextField
                                  fullWidth
                                  multiline
                                  maxRows={4}
                                  label="Notes"
                                  onChange={props.onChange}
                                  variant="standard"
                                  value={props.value}
                                />
                              );
                            }}
                          />
                        </FormControl>
                        <TooltipButton title="Add a description to help us manage your trips" />
                      </Grid>
                    </Grid>
                  )}
                </Box>
              )}
              {isReturnTripAvailable && (
                <FormControl
                  component="fieldset"
                  className={classes.formControl}
                >
                  <FormControlLabel
                    control={
                      <Controller
                        name="isReturnTrip"
                        control={control}
                        render={props => {
                          return (
                            <Checkbox
                              onChange={e => {
                                props.onChange(e.target.checked);
                              }}
                              checked={!!props.value}
                            />
                          );
                        }}
                      />
                    }
                    label={
                      <LabelWithTooltip
                        labelText={'Book a Return Trip'}
                        tooltipText={
                          'Check this box if you would like to create a return trip.  After saving we will prefill this page to enable you to easily create a second trip or return trip'
                        }
                      />
                    }
                  />
                </FormControl>
              )}

              <div className={classes.formActions}>
                {Object.keys(errors).length > 0 && (
                  <Typography
                    className={classes.formActionsValidation}
                    variant="body2"
                    color="error"
                  >
                    There are validation errors, please fix and try again.
                  </Typography>
                )}

                <Button
                  key="submitButton"
                  type="submit"
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  disabled={isSubmitting || disableSubmit}
                >
                  {isUpdate ? 'Update Transportation' : 'Book Transportation'}
                </Button>
              </div>
            </form>

            {isEhrPatientContext && (
              <>
                <Divider />
                <HelpInformation />
              </>
            )}
          </Paper>
        </Grid>

        {MapElement}
      </Grid>
      <ConfirmNotifyDialog
        open={shouldShowFulfillmentNotice}
        handleConfirm={() =>
          formValuesRef.current && onSubmit(formValuesRef.current)
        }
        handleClose={() => setShouldShowFulfillmentNotice(false)}
      />
    </Box>
  );
};
